<template>
  <b-container fluid class="theme-yellow">
    <div class="page-header">
      <Navbar text="Web AR" />
    </div>

    <section>
      <div class="row">
        <div class="col-sm-6">
          <h2 class="card-title">
            Immersive Embeds
            <StorageStatus
              :stored="embedsCount"
              :limit="embedsLimit"
            />
          </h2>
        </div>
        <div class="col-sm-6 text-right">
          <b-button
            variant="primary"
            v-b-modal="MODAL_ID_ADD_WEB_AR_EMBED"
            :disabled="!canCreateEmbed"
          >
            Create
          </b-button>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-7 col-lg-4 text-center">
          <div class="form-inline mb-3">
            <div class="form-group">
              <input
                type="search"
                name="filter"
                class="form-control"
                placeholder="Search..."
                v-model="filter"
              />
            </div>
          </div>
        </div>
      </div>

      <b-alert show variant="warning" class="mb-3">
        <strong>Please note:</strong> Anyone with access to the embed link can view and download the 3D model file. Do not share embed links publicly. Use them only for internal purposes, such as presentations or demos, and do not distribute the link to others.
      </b-alert>

      <div class="table-responsive">
        <b-table
          class="dataTable web-ar-embeds-table"
          striped
          :fields="fields"
          :items="embeds"
          :busy="isBusy"
          :filter="filter"
        >
          <div slot="table-busy" class="text-center my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>

          <template #cell(model)="data">
            <span>{{ data.item.model.modelName }}</span>
          </template>

          <template #cell(embedCode)="data">
            <b-button
              size="sm"
              @click="showWebAREmbedCodeModal(data.item)"
            >
              Embed Code
            </b-button>
          </template>
          
          <template #cell(actions)="data">
            <a
              href="javascript: void(0);"
              class="d-block d-md-inline"
              @click="onRemoveEmbedButtonClicked(data.item.id)"
            >
              <span class="icon icon-trash" />
            </a>
          </template>
        </b-table>
      </div>
    </section>

    <!-- Modals -->

    <AddWebAREmbedModal
      :modal-id="MODAL_ID_ADD_WEB_AR_EMBED"
      v-on:web-ar-embed-posted="onEmbedPosted"
      v-on:web-ar-embed-post-failed="onEmbedPostFailed"
    />

    <WebAREmbedCodeModal
      :modal-id="MODAL_ID_WEB_AR_EMBED_CODE"
      ref="WebAREmbedCodeModal"
    />
  </b-container>
</template>

<script>
import Navbar from "../Navigation/Navbar";
import StorageStatus from "@/components/Views/StorageStatus";
import AddWebAREmbedModal from "@/components/Views/AddWebAREmbedModal";
import WebAREmbedCodeModal from "@/components/Views/WebAREmbedCodeModal";
import * as harpraApi from "@/services/api.service";
import * as routerUtils from "@/utils/router-utils.js";
import * as stringUtils from "@/utils/string-utils";
import * as formatUtils from "@/utils/format-utils";
import modals from '@/enums/modal-types'
import { mapGetters } from "vuex";

export default {
  name: "WebAREmbeds",
  data() {
    return {
      embeds: [],
      fields: [
        { key: "title", label: "Title", sortable: true },
        { key: "description", label: "Description" },
        { key: "model", label: "Model" },
        {
          key: "creationDate",
          label: "Date Created",
          formatter: "formatDate",
          sortable: true,
        },
        { key: "embedCode", label: "Embed Code", sortable: false },
        { key: "actions", label: "Actions" },
      ],
      isBusy: true,
      filter: "",
      embedsCount: null,
      embedsLimit: null,
      canCreateEmbed: false,
      MODAL_ID_ADD_WEB_AR_EMBED: modals.addWebAREmbedModal,
      MODAL_ID_WEB_AR_EMBED_CODE: modals.webAREmbedCodeModal,
    };
  },
  components: {
    AddWebAREmbedModal,
    WebAREmbedCodeModal,
    Navbar,
    StorageStatus,
  },
  computed: {
    ...mapGetters([ "subscriptionInfo" ]),
    stringUtils() {
      return stringUtils;
    },
  },
  methods: {
    formatDate: formatUtils.formatDate,
    async loadEmbeds() {
      this.isBusy = true;
      try {
        this.embeds = await harpraApi.getWebAREmbeds();
        this.embedsCount = this.embeds.length;
      } finally {
        this.isBusy = false;
        this.canCreateEmbed = this.embedsCount < this.embedsLimit;
      }
    },
    showWebAREmbedCodeModal(embed) {
      this.$refs.WebAREmbedCodeModal.show(embed.id, embed.title);
    },
    async removeEmbed(embedId) {
      await harpraApi.deleteWebAREmbed(embedId);
    },
    async onEmbedPosted() {
      await this.loadEmbeds();
    },
    async onEmbedPostFailed() {
      await this.loadEmbeds();
    },
    onRemoveEmbedButtonClicked(embedId) {
      this.$bvModal
        .msgBoxConfirm("Are you sure you want to remove this embed?", {
          title: "Remove Embed",
          size: "sm",
          okVariant: "danger",
          okTitle: "YES",
          cancelVariant: "black",
          cancelTitle: "NO",
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
          modalClass: "regular",
        })
        .then(async (confirmed) => {
          if (!confirmed) {
            return;
          }

          await this.removeEmbed(embedId);
          await this.loadEmbeds();
        });
    },
  },
  async created() {
    if (this.subscriptionInfo?.isActive && this.subscriptionInfo?.isWebARActive) {
      this.embedsLimit = this.subscriptionInfo.webAREmbedsLimit;

      await this.loadEmbeds();

      if (this.$route.query["add"]) {
        routerUtils.removeQueryParam(this, "add");

        if (this.canCreateEmbed) {
          this.$bvModal.show(modals.addWebAREmbedModal);
        }
      }
    } else {
      routerUtils.routeToAccount(this);
    }
  },
  mounted() {},
};
</script>
